import { useEffect, useState } from "react";
import { ResBase } from "@shared/Api/ApiResponses";

interface UseAutoRefreshGetReturn<T> {
	/** If the hook is loading for the first time. */
	doingFirstLoad: boolean;
	/** Whether the last GET was successful or not. */
	successful?: boolean;
	/** The error msg of the last GET. */
	errorMsg?: string;

	data?: T;
}

export function useAutoRefreshGet<T>(
	/**
	 * Data get function. Be sure to make sure this doesn't change each render.
	 * Use useCallback or define it outside of the component.
	 */
	doFetch: () => Promise<ResBase<T>>,
	frequencySecs = 30
): UseAutoRefreshGetReturn<T> {
	const [data, setData] = useState<T>();
	const [doingFirstLoad, setDoingFirstLoad] = useState(true);
	const [successful, setSuccessful] = useState(false);
	const [errorMsg, setErrorMsg] = useState<string>();

	useEffect(() => {
		async function runFetch() {
			const res = await doFetch();

			setDoingFirstLoad(false);

			if (res.successful) {
				setData(res.data);
				setSuccessful(true);
			} else {
				setSuccessful(false);
				setErrorMsg(res.errorMsg);
			}
		}

		const interval = setInterval(async () => {
			await runFetch();
		}, frequencySecs * 1000);

		runFetch();

		return () => {
			clearInterval(interval);
		}; // Clean up.
	}, [doFetch, frequencySecs]);

	return {
		doingFirstLoad: doingFirstLoad,
		successful: successful,
		errorMsg: errorMsg,
		data: data
	};
}
